import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, Mail, Phone, MapPin, Facebook, Twitter, Linkedin } from 'lucide-react';
import { getSiteSettings } from '../services/api';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await getSiteSettings();
        setSettings(data);
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    loadSettings();
  }, []);

  return (
    <footer className="bg-gray-900 text-gray-300 dark:bg-gray-950 dark:text-gray-400">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            {settings?.logo_image ? (
              <div className="flex items-center gap-3">
                <img 
                  src={settings.logo_image} 
                  alt={settings?.logo_text || 'Logo'} 
                  className="object-contain"
                  style={{ 
                    height: `${settings?.logo_height || 48}px`,
                    width: settings?.logo_width ? `${settings.logo_width}px` : 'auto'
                  }}
                />
                {settings?.show_logo_text && (
                  <div className="hidden lg:block">
                    <h3 className="text-xl font-bold text-white">{settings?.logo_text || 'Otter River Properties'}</h3>
                    {settings?.tagline && (
                      <p className="text-sm text-gray-400">{settings.tagline}</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-otter-green">
                  <Home className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{settings?.logo_text || 'Otter River Properties'}</h3>
                  <p className="text-sm text-gray-400">{settings?.tagline || 'Lynchburg, VA'}</p>
                </div>
              </div>
            )}
            <p className="text-sm">
              {settings?.footer_about || 'Your trusted partner in property management for over 15 years.'}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {(settings?.nav_items || [
                { path: '/', label: 'Home' },
                { path: '/about', label: 'About Us' },
                { path: '/listings', label: 'Property Listings' },
                { path: '/qualifications', label: 'Qualifications' }
              ]).map((link, index) => (
                <li key={index}>
                  <Link to={link.path} className="text-sm hover:text-otter-green-300 transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin className="h-5 w-5 text-otter-green-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{settings?.address || '123 Main Street, Suite 200, Lynchburg, VA 24504'}</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-otter-green-400 flex-shrink-0" />
                <a href={`tel:${settings?.phone || '4345550123'}`} className="text-sm hover:text-otter-green-300 transition-colors">
                  {settings?.phone || '(434) 555-0123'}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-otter-green-400 flex-shrink-0" />
                <a href={`mailto:${settings?.email || 'info@cozyproperties.com'}`} className="text-sm hover:text-otter-green-300 transition-colors">
                  {settings?.email || 'info@cozyproperties.com'}
                </a>
              </li>
            </ul>
          </div>

          {/* Social & Hours - Disabled per user request */}
          {/* <div>
            <h4 className="text-white font-semibold mb-4">Connect With Us</h4>
            <div className="flex gap-3 mb-4">
              {(settings?.social_links || []).map((link, index) => (
                <a 
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-10 w-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-otter-green transition-colors"
                  title={link.platform}
                >
                  {link.platform.toLowerCase().includes('facebook') && <Facebook className="h-5 w-5" />}
                  {link.platform.toLowerCase().includes('twitter') && <Twitter className="h-5 w-5" />}
                  {link.platform.toLowerCase().includes('linkedin') && <Linkedin className="h-5 w-5" />}
                </a>
              ))}
            </div>
            <div className="text-sm space-y-1">
              <p className="text-white font-medium">Office Hours:</p>
              {(settings?.office_hours || ['Mon-Fri: 9:00 AM - 5:00 PM', 'Sat: 10:00 AM - 2:00 PM', 'Sun: Closed']).map((hour, index) => (
                <p key={index}>{hour}</p>
              ))}
            </div>
          </div> */}
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-800 text-center text-sm">
          <p>&copy; {currentYear} {settings?.logo_text || 'Otter River Properties'}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};